import { useEffect, useState } from "react";
import { useDataProvider, useRecordContext } from "react-admin";
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";

import { studentRequests } from "api/studentApi";
import { PreparatoryClassTypeChoices, WeekDayChoices } from "utils/choices";
import { useNotifyError } from "utils/useNotifyError";

const PreparatoryScheduleExpanded = ({ record: recordProp }) => {
  const contextRecord = useRecordContext();
  const record = recordProp ?? contextRecord;
  const dataProvider = useDataProvider();
  const notifyError = useNotifyError();
  const [schedule, setSchedule] = useState(null);

  useEffect(() => {
    if (!record?.id) return undefined;

    let cancelled = false;

    const load = async () => {
      try {
        const { data } = await dataProvider.customRequest(studentRequests.getPreparatorySchedule(record.id));
        if (!cancelled) setSchedule(data);
      } catch (error) {
        if (!cancelled) notifyError(error);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [record?.id, dataProvider, notifyError]);

  if (!schedule) return null;

  return (
    <Box p={2}>
      {schedule.disciplines?.length ? (
        schedule.disciplines.map(discipline => (
          <Box
            key={discipline.disciplineId}
            mb={3}
          >
            <Typography
              color="primary"
              variant="subtitle1"
            >
              {discipline.disciplineNameBg} ({formatHorarium(discipline)})
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Вид занятие</TableCell>
                  <TableCell>От дата</TableCell>
                  <TableCell>До дата</TableCell>
                  <TableCell>Час</TableCell>
                  <TableCell>Дни от седмицата</TableCell>
                  <TableCell>Учебни групи</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {discipline.periods.map(period => (
                  <TableRow key={period.id}>
                    <TableCell>{classTypeName(period.classTypeId)}</TableCell>
                    <TableCell>{period.fromDate}</TableCell>
                    <TableCell>{period.toDate}</TableCell>
                    <TableCell>
                      {period.startTime} - {period.endTime}
                    </TableCell>
                    <TableCell>{weekDaysText(period.weekDays)}</TableCell>
                    <TableCell>{period.studyGroups || "Всички"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ))
      ) : (
        <Typography color="secondary">Няма въведени дисциплини.</Typography>
      )}
    </Box>
  );
};

// An empty week-day set means the period runs on every day of its range.
const weekDaysText = weekDays =>
  weekDays?.length
    ? weekDays.map(day => WeekDayChoices.find(c => c.id === day)?.name).filter(Boolean).join(", ")
    : "Всеки ден";

const classTypeName = classTypeId => PreparatoryClassTypeChoices.find(c => c.id === classTypeId)?.name || "N/A";

const formatHorarium = discipline =>
  [
    `лекции: ${discipline.lecturesHorarium ?? "-"} ч.`,
    `упражнения: ${discipline.exercisesHorarium ?? "-"} ч.`,
  ].join(", ");

export default PreparatoryScheduleExpanded;
